import React, { forwardRef, useState } from 'react';
import { Person } from 'react-bootstrap-icons';

import { Button, Image, Modal, message } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';

import ImgCrop from 'antd-img-crop';

import { NotificationsTypes } from '@/interfaces/notificationsTypes';
import profileService from '@/services/profile';
import { CDN_BASE_URL } from '@/settings';
import styled, { css, StyledProp } from '@/styled';

interface ImageUploaderProps extends StyledProp {
  width: number;
  height: number;
  preview?: boolean;
  imageUrl?: string;
  updateAvatar: (avatar: string | undefined) => void;
  notification: (message: string, type: NotificationsTypes) => void;
}

const ImageUploader = forwardRef(
  ({ width, height, preview, imageUrl, updateAvatar, className }: ImageUploaderProps, ref) => {
    const [modal, setModal] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [url, setUrl] = useState<string>('');
    const [avatar, setAvatar] = useState<string | null>(imageUrl || null);

    const uploadImage = async (options: any) => {
      const { file, onSuccess, onError } = options;
      const formData = new FormData();
      formData.append('file', file, 'upload-image.png');

      try {
        const response = await profileService.uploadAvatar(formData);
        onSuccess(response);
      } catch (err) {
        onError('Error ao enviar imagem');
      }
    };

    const uploadProps: UploadProps = {
      name: 'file',
      multiple: false,
      customRequest: uploadImage,
      accept: 'image/png, image/jpeg',
      onChange(info) {
        const { status, response } = info.file;

        if (status === 'done') {
          message.success(`${info.file.name} arquivo enviado com sucesso.`);
          updateAvatar(response);
          setUrl(`${CDN_BASE_URL}${response}`);
          setFileList(info.fileList);
        }

        if (status === 'error') {
          message.error(`${info.file.name} erro ao enviar o arquivo.`);
        }
      }
    };

    const handleOpenModal = () => {
      setFileList([]);
      setModal(!modal);
    };

    const handleChangeImage = () => {
      setAvatar(url);
      handleOpenModal();
    };

    const handleRemoveImage = () => {
      setAvatar(null);
      updateAvatar(undefined);
    };

    return (
      <div className={className}>
        <div className='imageUploader-container'>
          {avatar ? <Image width={width} height={height} preview={preview} src={avatar} /> : <Person size={width} />}
          <div className='imageUploder-actions'>
            <div className='imageUploader-buttons'>
              <Button ghost className='imageUploader-btnChange' onClick={handleOpenModal}>
                Alterar foto
              </Button>
              {avatar && (
                <Button ghost className='imageUploader-btnChange' onClick={handleRemoveImage}>
                  Remover foto
                </Button>
              )}
            </div>
          </div>
        </div>
        <Modal open={modal} title='Selecionar imagem' footer={null} onCancel={handleOpenModal} className={className}>
          <div className='imageUploader-cropper'>
            {fileList.length < 1 ? (
              <ImgCrop modalTitle='Cortar imagem' modalOk='Cortar' modalCancel='Cancelar'>
                <Dragger {...uploadProps}>
                  <p className='ant-upload-text'>Clique aqui ou arraste uma imagem</p>
                  <p className='ant-upload-hint'>
                    O arquivo deve ser JPG ou PNG, e não deve exceder 2MB. A imagem será redimensionada para {width}x
                    {height}
                  </p>
                </Dragger>
              </ImgCrop>
            ) : (
              <Image width={width} height={height} src={url} preview={false} />
            )}
          </div>
          <div className='imageUploader-action'>
            <Button onClick={handleOpenModal}>Cancelar</Button>
            <Button type='primary' onClick={handleChangeImage} disabled={url ? false : true}>
              Atualizar
            </Button>
          </div>
        </Modal>
      </div>
    );
  }
);

export default styled(ImageUploader)(
  ({ theme }) => css`
    .imageUploader-container {
      padding: ${theme.space.spacing(4)};
      border: 1px solid ${theme.colorTextQuaternary};
      border-radius: 5px;
      cursor: pointer;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        color: black;
      }
    }

    .imageUploder-actions {
      position: absolute;
      top: 0;
      left: 0;
      background-color: rgb(0, 0, 0, 0.6);
      width: 100%;
      height: 100%;
      border-radius: 5px;
      opacity: 0;
      transition: opacity 0.5s;
    }

    .imageUploder-actions:hover {
      opacity: 1;
    }

    .imageUploader-buttons {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      flex-direction: column;
    }

    .imageUploader-btnChange,
    .imageUploader-btnTrash {
      color: ${theme.palette.gray[0]};
      border-radius: 0;
      border-color: ${theme.palette.gray[0]};
    }

    .imageUploader-btnChange {
      margin-bottom: ${theme.space.spacing(4)};
    }

    .imageUploader-cropper {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-top: ${theme.space.spacing(4)};
    }

    .imageUploader-action {
      display: flex;
      justify-content: flex-end;
      padding-top: ${theme.space.spacing(4)};
      padding-bottom: ${theme.space.spacing(4)};
      gap: ${theme.space.spacing(4)};
    }
  `
);
